const surface = require('../surface/surface');

// An in-page popupEdit host: the fetched components render inside a `container` component
// on the page instead of in a modal (e.g. an "add comment" composer under a ticket's
// conversation thread). See surface.js for the contract.
//
// The form problem, and why the form lives on <body>. The container sits inside the page's
// mainForm, so the surface cannot wrap its content in a <form> of its own — nesting a form
// in a form is not valid markup, and would make the submit scope an accident of DOM
// ancestry. Instead the surface creates an EMPTY body-level <form> and every hosted control
// points at it through the html `form` attribute (component.associateFormOwner(), driven by
// the data-zit-form-id this surface stamps on). Per the form-owner algorithm that attribute
// beats the nearest-ancestor-form rule, so:
//   - mainForm.elements never contains the hosted controls, and the page's own save cannot
//     serialize them (their html names carry no form-id prefix, so they would collide);
//   - document.getElementById(<surface formId>).submit() serializes exactly them —
//     multipart file uploads included, which is what the loud POST path needs.
const inlineSurface = Object.assign({}, surface, {
    subelements: {
        body: null
    },
    // Same zone names the modal exposes, so a controller's actionsConfig is host-agnostic.
    positions: ['footerLeft', 'footerRight'],
    defaultPosition: 'footerRight',
    container: false,

    // options: { container } — the container component this surface is dropped into.
    init: function (options) {
        options = options || {};

        this.container = options.container;

        // The empty, body-level form that owns the hosted controls. Never nested: it is a
        // sibling of the page's mainForm, exactly like the modal's.
        this.formElement = document.createElement('form');
        document.body.append(this.formElement);

        this.element = this.hm.helper.string2element(this.hm.helper.getTemplate('zit-inline-surface'));
        this.subelements.body = this.element.querySelector('[data-zit-subelement-type="body"]');

        // The template's own cancel control, mirroring the modal template's close button.
        // A server-declared cancel action could not name this surface: the container's html
        // id is prefixed with the form id of whatever rendered it, which the popupEdit
        // request never learns.
        this.element.querySelectorAll('[data-zit-dismiss="surface"]').forEach((dismissElement) => {
            dismissElement.addEventListener('click', () => { this.close(); });
        });

        // Re-opening replaces whatever was there (and deregisters its components).
        this.container.clear();
        this.container.hostedSurface = this;
        this.container.addElement(this.element);
    },

    // The container already revealed itself when the content landed in it.
    open: function () {
    },

    close: function () {
        this.dispose();
    },

    // Gone once the container was emptied — by close(), or by anything else that calls
    // container.clear().
    needsRebuild: function () {
        return this.disposed || this.element === null || !this.element.isConnected;
    },

    dispose: function () {
        if (this.disposed) {
            return;
        }

        this.disposed = true;

        if (this.container !== false && this.container.hostedSurface === this) {
            // Hand the teardown to the container: it deregisters our components, empties
            // itself and hides again. Detach first so its clear() does not call back here.
            this.container.hostedSurface = false;
            this.container.clear();
        } else if (this.element !== null) {
            this.hm.app.removeComponents(this.hm.app.elementSubComponents(this.element));
            this.element.remove();
        }

        if (this.formElement !== null) {
            this.formElement.remove();
            this.formElement = null;
        }
    }
});

module.exports = inlineSurface;
